import type { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { useMemo, useRef } from "react";
import { useApiQuery } from "../../hooks/use-api-query";
import { useDebounce } from "../../hooks/use-debounce";
import type { PaginatedTestsResponse } from "../../types";
import type { TestCase } from "./types";

interface UseTestCasesDataOptions {
  searchQuery: string;
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  tags: string[];
  filterType: string;
}

const PREDEFINED_FILTERS = ["all", "production", "local"];

export const useTestCasesData = ({
  searchQuery,
  paginationModel,
  sortModel,
  tags,
  filterType,
}: UseTestCasesDataOptions) => {
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("paginated", "true");
    params.set("limit", paginationModel.pageSize.toString());
    params.set("offset", (paginationModel.page * paginationModel.pageSize).toString());

    if (sortModel.length > 0) {
      const sortItem = sortModel[0];
      params.set("order_by", sortItem.field);
      params.set("order_direction", sortItem.sort || "desc");
    }

    if (debouncedSearchQuery) {
      params.set("q", debouncedSearchQuery);
    }

    if (tags.length > 0) {
      params.set("tags", tags.join(","));
    }

    // Apply filter type
    if (filterType === "production" || filterType === "local") {
      params.set("cfg_type", filterType);
    } else if (filterType && !PREDEFINED_FILTERS.includes(filterType)) {
      // It's an app config ID
      params.set("app_config_id", filterType);
    }

    return `/tests?${params.toString()}`;
  }, [paginationModel, sortModel, debouncedSearchQuery, tags, filterType]);

  const { data, isLoading, error } = useApiQuery<PaginatedTestsResponse>(queryString);

  // Keep track of the last known total to prevent DataGrid from resetting during loading
  const lastTotalRef = useRef<number>(0);
  if (data?.total !== undefined) {
    lastTotalRef.current = data.total;
  }

  // Convert tests to test cases
  const testCases = useMemo(() => {
    const items = data?.items ?? [];
    return items.map((test) => ({
      id: test.id,
      name: test.name,
      type: "test" as const,
      status: test.run_status || test.status,
      priority: test.priority || "low",
      severity: test.severity || "low",
      created_at: test.created_at,
      created_by: test.created_by,
      source: test.source,
      run_status: test.run_status,
      test_data: test,
      tags: test.tags || [],
      app_config: test.app_config
        ? {
            id: test.app_config.id,
            name: test.app_config.name,
            cfg_type: test.app_config.cfg_type,
            created_by: test.app_config.created_by,
          }
        : undefined,
    })) as TestCase[];
  }, [data?.items]);

  return {
    testCases,
    // Use the last known total during loading to prevent DataGrid pagination reset
    total: data?.total ?? lastTotalRef.current,
    isLoading,
    error,
  };
};
